import React, { useMemo, useRef } from 'react';
import useResizeObserver from 'Hooks/useResizeObserver';
import ReactECharts from 'echarts-for-react';
import get from 'lodash/get';
import {
  CHART_COLORS,
  INSIGHT_CHART_FONT_CHAR_WIDTH,
} from 'utils/constants/dashboard';
import { formatChartsTooltip } from '../helpers';
import { LineChartProps } from '../chartBuilder';
import {
  CHART_EMPTY_STATE_COLORS,
  CHART_STYLING,
} from '../constants/chartColors';
import { horizontalLineEmptyState } from '../constants/chartEmptyStates';

const GenericHorizontalLineChart: React.FC<LineChartProps> = ({
  chartData,
  onDrillDown,
  xAxisFieldName,
  yAxisFieldNames,
  legendNames,
  minInterval,
}) => {
  const containerRef = useRef<any>();
  const { height, width } = useResizeObserver(containerRef);

  const onEvents = useMemo(
    () => ({
      click: () => {
        onDrillDown && onDrillDown();
      },
    }),
    [onDrillDown]
  );

  const options = useMemo(() => {
    // Handle empty data case
    if (chartData.length === 0) {
      return horizontalLineEmptyState();
    }

    let yAxisCharCount = Math.max(
      ...chartData.map((i) =>
        Math.max(
          ...yAxisFieldNames.map((j) => get(i, j, '').toLocaleString().length)
        )
      )
    );
    let yAxisWidth = Math.min(
      120,
      yAxisCharCount * INSIGHT_CHART_FONT_CHAR_WIDTH
    );
    let remainingWidth = width - yAxisWidth;
    let numOfTicks = Math.min(
      chartData.length,
      Math.floor(remainingWidth / 72.0)
    ); // 72 = tick size plus margin.
    let expandedTickSize =
      numOfTicks > 0
        ? (remainingWidth - (numOfTicks - 1) * 24) / numOfTicks
        : remainingWidth;

    const legendHeight = yAxisFieldNames.length > 1 ? 24 : 0;

    const dataZoomOptions = [];
    if (numOfTicks < chartData.length) {
      dataZoomOptions.push({
        type: 'slider',
        startValue: 0,
        endValue: Math.max(0, numOfTicks - 1),
        height: 0,
        bottom: legendHeight + 12,
        zoomLock: true,
        showDetail: false,
        moveHandleSize: 12,
        moveHandleStyle: {
          color: CHART_EMPTY_STATE_COLORS.DATA_ZOOM_HANDLE,
          opacity: CHART_STYLING.FULL_OPACITY,
        },
      });
      dataZoomOptions.push({
        type: 'inside',
        orient: 'horizontal',
        startValue: 0,
        endValue: Math.max(0, numOfTicks - 1),
        zoomLock: false,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: true,
        moveOnMouseMove: true,
      });
    }

    return {
      dataZoom: dataZoomOptions,
      grid: {
        show: false,
        containLabel: true,
        top: 8,
        bottom:
          numOfTicks < chartData.length ? legendHeight + 20 : legendHeight,
        right: 0,
        left: 0,
      },
      xAxis: {
        type: 'category',
        data: chartData.map((i) => get(i, xAxisFieldName)),
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          interval: 0,
          margin: 4,
          hideOverlap: false,
          width: expandedTickSize,
          overflow: 'truncate',
        },
      },
      yAxis: {
        type: 'value',
        minInterval: minInterval || 0,
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          align: 'right',
          width: yAxisWidth,
          overflow: 'truncate',
          margin: 0,
        },
      },
      series: yAxisFieldNames.map((i, idx) => ({
        type: 'line',
        data: chartData.map((j) => get(j, i)),
        name: legendNames.length >= idx ? legendNames[idx] : undefined,
        showSymbol: false,
      })),
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: formatChartsTooltip,
      },
      legend: {
        show: yAxisFieldNames.length > 1,
        type: 'scroll',
        orient: 'horizontal',
        bottom: 0,
        left: 0,
        textStyle: {
          overflow: 'truncate',
        },
      },
      textStyle: {
        fontFamily: 'inherit',
      },
      color: CHART_COLORS,
    };
  }, [
    chartData,
    width,
    yAxisFieldNames,
    minInterval,
    xAxisFieldName,
    legendNames,
  ]);

  return (
    <div className='h-full w-full' ref={containerRef}>
      {width > 0 && (
        <ReactECharts
          key={`${width}-${height}`}
          opts={{ renderer: 'svg' }}
          option={options}
          style={{ height: height, width: width }}
          onEvents={onEvents}
        />
      )}
    </div>
  );
};

export default GenericHorizontalLineChart;
